import cn from 'classnames'
import {
  getPaymentState,
  getAreaInfoById,
  changeUserEmail,
  getEventInfoById,
  startEventPaymentAsync,
  resetEventCost,
} from '@/store/slices/stagesSlice/stagesSlice';
import { Container, Button, Card, Title, Text, CheckBox} from '@/components';
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store'
import { useRouter } from 'next/router'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import CarNumberLabel from '../PaymentPage/CarNumberLabel'
import unicode from '@/helpers/unicodeChars'
import styles from './EventPaymentPage.module.scss'
import EmailInput from '@/components/ui/EmailInput'
import { supportPhoneLink } from '@/constants';
import { formatEventPeriod } from '@/helpers/formatEventPeriod';
import getLinkHref from '@/helpers/getLinkHref';
import formatNumberToString from '@/helpers/formatNumberToString';
import { getConsentCheckboxTitle } from '@/helpers/getConsentCheckboxTitle';

export default function EventPaymentPage({ eventId, number }: { eventId: string; number: string }) {
  const fieldRef = useRef(null)
  const reduxState = useSelector<RootState, RootState>(state => state)
  const dispatch = useDispatch()
  const router = useRouter()
  const { loading } = getPaymentState(reduxState)
  const [error, setError] = useState<string>('')
  const [emailValue, setEmailValue] = useState<string>('')
  const [emailInputError, setEmailInputError] = useState<string>('')
  const { data: areaInfo } = getAreaInfoById(reduxState, (router.query?.areaId as string) || null)
  const { data: eventInfo } = getEventInfoById(reduxState, eventId)
  const eventCost = reduxState.stages.eventCost.data
  const eventCostError = reduxState.stages.eventCost.error
    ? String(reduxState.stages.eventCost.error)
    : undefined
  const period =
    eventInfo?.start_at && eventInfo?.end_at
      ? formatEventPeriod(eventInfo.start_at, eventInfo.end_at)
      : 0

  const [isConsentParkingRules, setIsConsentParkingRules] = useState(true)
  const [parkingRulesError, setParkingRulesError] = useState('')

  const onChangeParkingRules = () => {
    setParkingRulesError('')
    setIsConsentParkingRules(prev => !prev)
  }

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleStartPayment()
    }
  }

  const handleStartPayment = async () => {
    const onSuccess = (orderNumber: string, eventId: string) => {
      const paymentPendingPageHref = getLinkHref({
        type: 'payment-pending',
        data: { orderNumber, eventId },
      })

      router.push(paymentPendingPageHref)
    }

    if (emailValue && !emailValue.match(/.+[@].+/gm)) {
      setEmailInputError('Укажите корректный email')
      return
    }

    const email = emailValue.trim()
    await dispatch(changeUserEmail({ email }))

    if (!isConsentParkingRules) {
      setParkingRulesError('Подтвердите ознакомление с правилами пользования парковкой')
      return
    }

    if (eventCostError) return

    await dispatch(
      startEventPaymentAsync({
        eventId,
        number,
        email,
        permissions: {
          data_processing: !!email,
          parking_rules: isConsentParkingRules
        },
        onSuccess,
        onError: (message: string) => setError(message),
      }),
    )
  }

  useEffect(() => {
    if (emailInputError) {
      const field = (fieldRef.current as unknown) as HTMLInputElement
      field?.focus()
    }
  }, [emailInputError])

  useEffect(() => {
    return () => {
      dispatch(resetEventCost())
    }
  }, [])

  if (!eventId)
    return (
      <Container>
        <Card color="decor">
          <div className={styles.notFound}>
            <div className={styles.notFound_Image}>
              <img src="/images/subscription-not-found.svg" alt="" />
            </div>
            <span>Нет информации по данному абонементу</span>
          </div>
        </Card>
      </Container>
    )

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Container>
          {period && (
            <header className={styles.header}>
              <Text className={styles.interval} isBold>
                Длительность мероприятия: {period}
              </Text>
              <CarNumberLabel number={number} className={styles.carNumber} />
            </header>
          )}
          <Title level={4} className={styles.paymentDetails_title}>
            К оплате
          </Title>
          <div className={styles.paymentDetails_row}>
            <Text level={2}>{eventInfo.name}</Text>
            <Text level={2} className={styles.paymentDetails_price}>
              {formatNumberToString(eventCost?.event.amount ?? 0)}
              {unicode.no_break_space}
              {unicode.ruble}
            </Text>
          </div>
          <div className={styles.totalAmount}>
            <p>Итого: </p>
            <p>
              {formatNumberToString(eventCost?.total_amount ?? 0)}
              {unicode.no_break_space}
              {unicode.ruble}
            </p>
          </div>
        </Container>

        <Container className={styles.email_container}>
          <Title className={styles.content__title} level={4} size={24}>
            Укажите ваш email
          </Title>

          <Text level={2} className={styles.content__paragraph}>
            Ваш email необходим, чтобы мы могли прислать чек об успешной оплате.
          </Text>

          <label className={cn(styles.input, { [styles['input--active']]: !!emailValue })}>
            <EmailInput
              onChange={setEmailValue}
              onKeyPress={handleKeyPress}
              value={emailValue}
              error={emailInputError || error || eventCostError}
            />
          </label>
        </Container>

        <Container>
          <div className={styles.checkBoxWrapper}>
            <CheckBox
              name="rulesUsingParking"
              checked={isConsentParkingRules}
              title={
                getConsentCheckboxTitle({
                  type: 'parkingRules',
                  linkParkingRules: areaInfo?.link_parking_rules,
                }) ?? ''
              }
              onChange={onChangeParkingRules}
            />
            {parkingRulesError && <Text className={styles.error}>{parkingRulesError}</Text>}
          </div>

        </Container>
      </div>

      <Container className={styles.buttons}>
        <Button
          withLoader
          onClick={handleStartPayment}
          className={styles.button}
          loading={loading}
          background={areaInfo?.customising.button_back_color}
          disabled={!!eventCostError}
        >
          Оплатить
        </Button>
        <Button component="a" href={supportPhoneLink} className={styles.button} isTransparent>
          Обратиться в поддержку
        </Button>
      </Container>
    </section>
  )
}
